import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { medicationQuestionnaireSchema, type MedicationQuestionnaireInput } from "@dosa/validation";
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, FieldLabel, Textarea } from "@dosa/ui";
import { useAuth } from "../../hooks/useAuth";
import { submitMedicationQuestionnaire } from "../../lib/api-client";

const yesNoFields: Array<keyof MedicationQuestionnaireInput> = [
  "takesMedicationsAsPrescribed",
  "missesDoses",
  "usesPillOrganizer",
  "experiencesSideEffects",
  "hasDifficultyAffordingMedications",
  "hasDifficultySwallowingOrAdministering",
  "usesOtherPharmacies",
];

export function PatientMtmIntakePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const patientId = user!.patientId!;
  const [submitted, setSubmitted] = useState(false);

  const { control, register, handleSubmit } = useForm<MedicationQuestionnaireInput>({
    resolver: zodResolver(medicationQuestionnaireSchema),
  });

  const mutation = useMutation({
    mutationFn: (input: MedicationQuestionnaireInput) => submitMedicationQuestionnaire(patientId, input),
    onSuccess: () => setSubmitted(true),
  });

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-charcoal-900">{t("patient.mtm.title")}</h1>
        <p className="mt-1 text-charcoal-500">{t("patient.mtm.subtitle")}</p>
      </div>

      {submitted && <Alert variant="success" title={t("patient.mtm.questionnaireTitle")}>{t("patient.dashboard.bookAppointment")}</Alert>}

      <Card>
        <CardHeader>
          <CardTitle>{t("patient.mtm.questionnaireTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-5">
            {yesNoFields.map((field) => (
              <label key={field} className="flex items-center justify-between gap-4 rounded-md border border-charcoal-100 p-3">
                <span className="text-sm text-charcoal-700">{t(`patient.mtm.questions.${field}`, field.replace(/([A-Z])/g, " $1"))}</span>
                <Controller
                  control={control}
                  name={field}
                  render={({ field: f }) => (
                    <input
                      type="checkbox"
                      checked={!!f.value}
                      onChange={(e) => f.onChange(e.target.checked)}
                      className="h-5 w-5 rounded border-charcoal-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  )}
                />
              </label>
            ))}
            <div>
              <FieldLabel htmlFor="otherNotes">{t("common.notes")}</FieldLabel>
              <Textarea id="otherNotes" rows={4} {...register("otherNotes")} />
            </div>
            <Button type="submit" isLoading={mutation.isPending} size="lg">
              {t("patient.mtm.submit")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
